import { clsx } from "clsx";
import { FunctionComponent, useMemo, useState } from "react";
import { CopyToClipboard } from "../../../../../../../components";
import { SortedGrid as Grid } from "../../../../../../../components/Grid";
import { Link } from "../../../../../../../components/link";
import Typography from "../../../../../../../components/typography/Typography";
import { useSelectRowsContext } from "../../../../../../../hooks";
import { sortGridByColumn } from "../../../../../../../utils";
import { Schema } from "../../../../../@types";
import { SortParams } from "../../../../../@types/rldynamicanalysis/yara";
import { createSelectedRowKey } from "../../../../../util";
import {
  IocRow,
  LLMSection,
  LLMSummary,
  LLMTextLink
} from "../@types/LLMSummary";
import { TYPE_TO_DISPLAY } from "../consts";
import styles from "../llm-summary.module.scss";
import LLMIocExportDropdown from "./LLMIocExportDropdown";
import { buildUrlForType } from "./utils";

type LLMIocGridProps = {
  summary?: LLMSummary["summary"];
};

const extractIocsFromSections = (summary?: LLMSummary["summary"]): IocRow[] => {
  if (!summary?.summary) return [];

  const sections = summary.summary;
  const iocMap = new Map<string, IocRow>();

  const sectionEntries: (LLMSection | null | undefined)[] = [
    sections.classification,
    sections.executive_summary,
    sections.file_behavior,
    sections.file_info,
    sections.network_references,
    sections.relationships
  ];

  sectionEntries.forEach(section => {
    if (section?.links) {
      section.links.forEach((link: LLMTextLink) => {
        const key = `${link.value}|${link.type}`;
        if (!iocMap.has(key) && TYPE_TO_DISPLAY[link.type]) {
          iocMap.set(key, {
            name: link.value,
            type: link.type
          });
        }
      });
    }
  });

  return Array.from(iocMap.values());
};

const NameCell: FunctionComponent<{ item: IocRow }> = ({ item }) => {
  const to = buildUrlForType(item.name, item.type);
  return (
    <div className={"overflow-ellipsis"}>
      {to ? <Link to={to}>{item.name}</Link> : <span>{item.name}</span>}
      <CopyToClipboard value={item.name} className={"ps-10"} />
    </div>
  );
};

const TypeCell: FunctionComponent<{ item: IocRow }> = ({ item }) => (
  <span>{TYPE_TO_DISPLAY[item.type] ?? item.type}</span>
);

const gridSchema: Schema<IocRow> = {
  fields: [
    {
      name: "name",
      displayTitle: "Name",
      component: item => <NameCell item={item} />,
      style: { width: "40%" },
      sortable: true
    },
    {
      name: "type",
      displayTitle: "Type",
      component: item => <TypeCell item={item} />,
      style: { width: "10%" },
      sortable: true
    }
  ]
};

const LLMIocGrid: FunctionComponent<LLMIocGridProps> = ({ summary }) => {
  const iocs = useMemo(() => extractIocsFromSections(summary), [summary]);

  const [sort, setSort] = useState<SortParams>({
    field: "name",
    direction: "asc"
  });

  const { api, selected } = useSelectRowsContext();

  const sortedIocs = useMemo(() => {
    if (sort.field === "default") return iocs;
    const sorted = [...iocs];
    sortGridByColumn(sorted, sort);
    return sorted;
  }, [iocs, sort]);

  const handleSortChange = (sortParams: SortParams) => {
    const { field, direction } = sort;
    if (sortParams.direction === direction && sortParams.field === field)
      return;
    setSort(sortParams);
  };

  if (iocs.length === 0) {
    return null;
  }

  return (
    <div className={clsx(styles.llm_summary_content_border, "p-10")}>
      <Typography variant={"h5"} className={"llm_text_gray"}>
        IoCs
      </Typography>

      <div className={"mt-20 mb-20 flex flex-center"}>
        <div>
          <Typography
            variant={"subtitle5"}
            className="llm_text_medium_gray"
            tag={"span"}
          >
            Showing
          </Typography>{" "}
          <Typography
            variant={"subtitle4"}
            className={"llm_text_gray"}
            tag={"span"}
          >
            {iocs.length} Results
          </Typography>
        </div>
        <div className="ms-auto">
          <LLMIocExportDropdown data={sortedIocs} selectedRows={selected} />
        </div>
      </div>

      <Grid
        data={sortedIocs}
        schema={gridSchema}
        emptyGridMsg="No IOCs found"
        cellClassName={"px-10"}
        tableProps={{
          className: styles.llm_summary_content_border
        }}
        headerCellClassName={"pt-10"}
        sort={sort}
        onSortChange={handleSortChange}
        selectableRows
        selectedRows={selected}
        onRowSelectChange={(row: IocRow, index: number) =>
          api.select(row, index, 1)
        }
        checkRowSelected={(row: IocRow, index: number) =>
          api.checkIsSelected(row, index, 1)
        }
        onSelectAllRowsChange={() => api.selectPage(sortedIocs, 1)}
        createSelectedRowKey={createSelectedRowKey}
        allRowsSelected={api.isPageSelected(sortedIocs, 1)}
      />
    </div>
  );
};

export default LLMIocGrid;
